/**
 * useWorkflowVersions — single hook that owns all async + lifecycle
 * effects for the Workflow Versions v2 surface.
 *
 * Backend audit (Commit 2):
 *   • Initial fetch    — GET /workflow/{thread_id}/versions?offset=0&limit=50
 *                        Returns { items, total, offset, limit,
 *                        current_version_number }.
 *   • Load more        — same endpoint with an incremented offset;
 *                        results appended to state.
 *   • Lazy diff fetch  — GET /workflow/{thread_id}/versions/{n}
 *                        Returns a full VersionRecord (with `diff`
 *                        populated). Called when a card expands.
 *   • Restore          — POST /workflow/{thread_id}/versions/{n}/restore
 *                        Returns { status, restored_from,
 *                        new_version_number, summary, snapshot_version,
 *                        diffs_applied, agents_affected }.
 *
 * No polling — versions are append-only and don't change
 * asynchronously. After a restore, the hook refetches page 1 so
 * the new restore-type version shows up at the top.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  getWorkflowVersions,
  getVersionDiff,
  restoreToVersion,
} from '../api';
import {
  initialVersionsState,
  RestoreResponse,
  VersionListResponse,
  VersionRecord,
  VersionsState,
} from '../state/types';
import { versionsReducer } from '../state/versionsReducer';

export interface UseWorkflowVersionsInput {
  threadId?: string | null;
  /** Optional callback fired after a successful restore so the
   *  parent (ProjectWorkspace) can refresh its cached agent
   *  results. Mirrors the legacy VersionsPanel prop. */
  onAfterRestore?: () => void;
}

export interface WorkflowVersionsActions {
  /** Initial fetch — replaces the list. */
  refetch: () => Promise<void>;
  /** Append the next page of versions to the existing list. */
  loadMore: () => Promise<void>;
  /** Toggle expand state for a version card. Lazy-fetches the
   *  detail (with full diff) the first time a card is opened. */
  toggleExpand: (versionNumber: number) => Promise<void>;
  /** Open the restore-confirmation dialog. */
  openRestoreConfirm: (versionNumber: number) => void;
  /** Close the restore-confirmation dialog without acting. */
  closeRestoreConfirm: () => void;
  /** Confirm restore — POSTs, refetches list, fires onAfterRestore. */
  confirmRestore: () => Promise<RestoreResponse | null>;
}

export function useWorkflowVersions({
  threadId,
  onAfterRestore,
}: UseWorkflowVersionsInput): {
  state: VersionsState;
  actions: WorkflowVersionsActions;
} {
  const [state, dispatch] = useReducer(versionsReducer, initialVersionsState);

  // Track in-flight detail fetches so we don't double-dispatch
  // DIFF_FETCH_START when the user collapses + re-expands quickly.
  // (The reducer's diffLoadingByNumber set is the source of truth
  // for "currently fetching"; this ref dedupes the action burst.)
  const inFlightDetailRef = useRef<Set<number>>(new Set());

  // ── Initial fetch when threadId becomes available ──────────────────
  const refetch = useCallback(async () => {
    if (!threadId) return;
    dispatch({ type: 'FETCH_START' });
    try {
      const response = (await getWorkflowVersions(threadId, {
        offset: 0,
        limit: 20,
      })) as unknown as VersionListResponse;
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: { response, append: false },
      });
    } catch (e: any) {
      dispatch({
        type: 'FETCH_ERROR',
        payload: e?.message ?? 'Failed to load workflow versions',
      });
    }
  }, [threadId]);

  useEffect(() => {
    if (!threadId) {
      dispatch({ type: 'RESET' });
      return;
    }
    refetch();
  }, [threadId, refetch]);

  // ── Load more (pagination) ────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!threadId) return;
    const nextOffset = state.pagination.offset + state.versions.length;
    if (nextOffset >= state.pagination.total) return;
    dispatch({ type: 'FETCH_START' });
    try {
      const response = (await getWorkflowVersions(threadId, {
        offset: nextOffset,
        limit: state.pagination.limit,
      })) as unknown as VersionListResponse;
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: { response, append: true },
      });
    } catch (e: any) {
      dispatch({
        type: 'FETCH_ERROR',
        payload: e?.message ?? 'Failed to load more versions',
      });
    }
  }, [threadId, state.pagination, state.versions.length]);

  // ── Toggle expand (lazy detail fetch) ──────────────────────────────
  const toggleExpand = useCallback(
    async (versionNumber: number) => {
      if (!threadId) return;
      dispatch({ type: 'TOGGLE_EXPAND', payload: versionNumber });

      // If we're collapsing, nothing else to do.
      if (state.expanded.has(versionNumber)) return;

      // If we already have the diff cached on the list record, skip
      // the fetch. The list endpoint includes diff for non-snapshot
      // entries — only snapshot rows omit it.
      const existing = state.byNumber[versionNumber];
      if (existing && existing.diff != null) return;

      // Snapshot rows: skip the detail fetch unless the consumer
      // explicitly asked for the snapshot payload. (Commit 8 covers
      // the snapshot-body branch; for now the cards just render the
      // operation label.)
      if (existing?.is_snapshot && existing.diff === null) return;

      if (inFlightDetailRef.current.has(versionNumber)) return;
      inFlightDetailRef.current.add(versionNumber);

      dispatch({ type: 'DIFF_FETCH_START', payload: versionNumber });
      try {
        const record = (await getVersionDiff(threadId, versionNumber, {
          includeSnapshot: false,
        })) as unknown as VersionRecord;
        dispatch({
          type: 'DIFF_FETCH_SUCCESS',
          payload: { versionNumber, record },
        });
      } catch {
        dispatch({ type: 'DIFF_FETCH_ERROR', payload: versionNumber });
      } finally {
        inFlightDetailRef.current.delete(versionNumber);
      }
    },
    [threadId, state.expanded, state.byNumber],
  );

  // ── Restore flow ─────────────────────────────────────────────────
  const openRestoreConfirm = useCallback((versionNumber: number) => {
    dispatch({ type: 'OPEN_RESTORE_CONFIRM', payload: versionNumber });
  }, []);

  const closeRestoreConfirm = useCallback(() => {
    dispatch({ type: 'CLOSE_RESTORE_CONFIRM' });
  }, []);

  const confirmRestore = useCallback(async (): Promise<RestoreResponse | null> => {
    if (!threadId || state.pendingRestore == null) return null;
    const target = state.pendingRestore;
    dispatch({ type: 'RESTORE_START' });
    try {
      const response = (await restoreToVersion(
        threadId,
        target,
      )) as unknown as RestoreResponse;
      // Refetch page 1 so the new restore version appears at the top.
      await refetch();
      onAfterRestore?.();
      return response;
    } catch {
      return null;
    } finally {
      dispatch({ type: 'RESTORE_END' });
    }
  }, [threadId, state.pendingRestore, refetch, onAfterRestore]);

  return {
    state,
    actions: {
      refetch,
      loadMore,
      toggleExpand,
      openRestoreConfirm,
      closeRestoreConfirm,
      confirmRestore,
    },
  };
}
